import React, { useCallback, useEffect, useState } from "react";
import { Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { api, type TopASNRow } from "../api";
import { useRefreshAll } from "../hooks/useRefreshAll";

// Reused from TopTalkersTable — duplicated here rather than exported so the
// two widgets stay independent and aren't coupled through shared helpers.
function fmtBps(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " G";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}
function fmtPps(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

interface Props {
  geo: "nix" | "ixp";  // NIX = domestic VN, IXP = international
  dir: "in" | "out";
  metric: "bps" | "pps";
  color?: string;
  refreshMs?: number;
  /** When set, scope the ASN rollup to a single sFlow source (per-sensor dashboard). */
  sensorId?: number;
}

const LIMIT_OPTIONS = [
  { label: "Top 10", value: 10 },
  { label: "Top 20", value: 20 },
  { label: "Top 50", value: 50 },
];

/**
 * TopASNTable — polls /api/v1/live/top/asn every refreshMs and renders the
 * top N remote ASNs for the given (geo, dir) slice. Mirrors the layout of
 * TopTalkersTable so the dashboard feels consistent.
 */
export default function TopASNTable({
  geo,
  dir,
  metric,
  color = "#1677ff",
  refreshMs = 5000,
  sensorId,
}: Props) {
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<TopASNRow[]>([]);
  // See TopTalkersTable for why this is `initialLoading` rather than a
  // per-refresh `loading` flag — eliminates the 5 s blink on the
  // dashboard while still showing a spinner on cold start.
  const [initialLoading, setInitialLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const rows = await api.liveTopASN(geo, dir, metric, limit, sensorId);
      setData(rows);
    } catch {
      /* keep stale on transient error */
    } finally {
      setInitialLoading(false);
    }
  }, [geo, dir, metric, limit, sensorId]);

  const refreshAllNonce = useRefreshAll();

  useEffect(() => {
    load();
    if (refreshMs > 0) {
      const id = window.setInterval(load, refreshMs);
      return () => window.clearInterval(id);
    }
  }, [load, refreshMs, refreshAllNonce]);

  const fmt = metric === "bps" ? fmtBps : fmtPps;
  const unitLabel = metric === "bps" ? "Bits/s" : "Pkts/s";

  const colorLight = React.useMemo(() => {
    const m = /^#([0-9a-f]{6})$/i.exec(color);
    if (!m) return color;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    const mix = (c: number) => Math.round(c + (255 - c) * 0.35);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  }, [color]);

  const columns: ColumnsType<TopASNRow> = [
    {
      title: "#",
      dataIndex: "rank",
      width: 40,
      align: "center",
      render: (v: number) => {
        const tier = v === 1 ? "top-1" : v === 2 ? "top-2" : v === 3 ? "top-3" : "";
        return <span className={`tt-rank ${tier}`}>{v}</span>;
      },
    },
    {
      title: "ASN",
      dataIndex: "asn",
      width: 90,
      render: (asn: number) => <Tag color="geekblue">AS{asn}</Tag>,
    },
    {
      title: "Organisation",
      dataIndex: "org",
      ellipsis: true,
      render: (org: string) => (
        <span className="tt-ip-link" title={org || "(unknown)"}>
          {org || <span style={{ color: "#999" }}>(unknown)</span>}
        </span>
      ),
    },
    {
      title: unitLabel,
      key: "value",
      width: 90,
      align: "right",
      render: (_: unknown, row: TopASNRow) => (
        <span
          style={{
            fontFamily: '"SF Mono", Monaco, Menlo, Consolas, monospace',
            fontSize: 12,
            fontWeight: 600,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmt(metric === "bps" ? row.bps : row.pps)}
        </span>
      ),
    },
    {
      title: (
        <Select
          size="small"
          value={limit}
          onChange={(v) => setLimit(v)}
          options={LIMIT_OPTIONS}
          style={{ width: 82, fontSize: 11 }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      dataIndex: "pct",
      width: 110,
      align: "right",
      render: (v: number) => (
        <div
          className="tt-bar-wrap"
          style={
            {
              ["--tt-color" as string]: color,
              ["--tt-color-light" as string]: colorLight,
              ["--tt-glow" as string]: color + "4d",
            } as React.CSSProperties
          }
        >
          <div className="tt-bar-track">
            <div className="tt-bar-fill" style={{ width: `${Math.min(v, 100)}%` }} />
          </div>
          <span className="tt-pct">{v.toFixed(0)}%</span>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => `${r.rank}-${r.asn}`}
      size="small"
      loading={initialLoading && data.length === 0}
      dataSource={data}
      columns={columns}
      pagination={false}
      scroll={{ y: "calc(100% - 36px)" }}
      locale={{ emptyText: "No live data" }}
      style={{ height: "100%" }}
    />
  );
}
